import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  Switch,
  Divider,
} from '@mui/material'
import {
  Search as SearchIcon,
  Edit as EditIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  UnarchiveOutlined as UnarchiveOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'

import { showModal } from 'actions/app'
import { get, noop, filter, find } from 'es-toolkit/compat'
import {
  DataGrid,
  type GridRowId,
  type GridRowSelectionModel,
  type GridComparatorFn,
} from '@mui/x-data-grid'

import { unitConvert } from '@farmtrx/units'
import { getClientsPaginated } from 'api/client'
import colors from 'common/colors'

import OCFFAddClientComponent from './add-client'
import OCFFEditClientComponent from './edit-client'
import OCFFDeleteClient from './delete-client'
import ArchiveClients from './archive-clients'
import RestoreClients from './restore-clients'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'

import type { AppState } from 'state/index'
import type { FieldModel } from 'state/field'

declare module 'components/cff/clients/index.js' {
  type featuresOverviewType = {
    id: number
    geometry: object
    properties: object
  }

  type fieldAreaMapType = {
    id: number
    area: number
    farmId: number
  }

  type RowData = {
    id: number
    areaSqm: string | 0
    name: string
    farmCount: number
    fieldCount: number
  }

  type clientColumns = {
    field: string
    headerName: string
    minWidth: number
    maxWidth: number
    flex: number
    sortComparator?: GridComparatorFn<string>
  }
}
export default function OCFFClientsComponent(): ReactElement<any, any> {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const fields: FieldModel[] = useSelector((state) =>
    get(state, 'field.collection', []),
  )
  const farms = useSelector((state) =>
    get(state as AppState, 'farm.collection', []),
  )
  const userPrefs = useSelector((state) =>
    get(state as AppState, 'account.singleton.prefs'),
  )
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const permissions = useSelector((state) =>
    get(state as AppState, 'account.permissions'),
  )
  const showClients = permissions.includes('farm:read')
  const addModifyClients = permissions.includes('farm:write')

  const [columns, setColumns] = useState<clientColumns[]>([])

  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
  })
  const [rows, setRows] = useState<RowData[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [loading, setLoading] = useState(false)
  const [reloadClients, setReloadClients] = useState(0)

  const reload = () => setReloadClients((r) => r + 1)

  const onSearch = (e) => {
    setSearchValue(e.target.value)
  }
  const [showActiveClients, setShowActiveClients] = useState(true)
  const fieldAreaSort: GridComparatorFn<string> = (v1, v2) => {
    const area1 = parseFloat(v1.split(' ')[0]) || 0
    const area2 = parseFloat(v2.split(' ')[0]) || 0
    return area1 - area2
  }

  // Debounce searchValue
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    }
  }, [searchValue])

  const filterClients = (searchValue, client) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()
      return client.name.toLowerCase().indexOf(lowerSearchValue) > -1
    }
    return true
  }

  const showAddClientPopup = useCallback(() => {
    dispatch(showModal(<OCFFAddClientComponent onSuccess={reload} />))
  }, [])

  useEffect(() => {
    const _columns: clientColumns[] = [
      {
        field: 'name',
        headerName: t('client'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'farmCount',
        headerName: t('farms_quantity'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'fieldCount',
        headerName: t('fields_quantity'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'areaSqm',
        headerName: t('field_area'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        sortComparator: fieldAreaSort,
      },
    ]
    setColumns(_columns)
    setRowSelectionModel({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  }, [])

  function CustomNoRowsOverlayFields(): ReactElement {
    if (showClients) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {t('no_clients')}
        </div>
      )
    } else
      return (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          {t('no_permissions', { item: t('clients') })}
        </div>
      )
  }

  // Fetch paginated clients
  useEffect(() => {
    let ignore = false
    setLoading(true)
    if (showClients) {
      ;(dispatch as any)(
        getClientsPaginated(
          (paginationModel.page || 0) + 1,
          paginationModel.pageSize,
          debouncedSearch,
          !showActiveClients,
        ),
      )
        .then((res: any) => {
          if (!ignore) {
            const { results, pagination } = res.data
            // Map to DataGrid rows using server-provided values
            const _rows = (results || []).map((client) => ({
              id: client.id,
              areaSqm: client.areaSqm
                ? `${unitConvert
                    .convertAreaToPref(
                      client.areaSqm,
                      userPrefs['units']['area_in'],
                    )
                    .toFixed(2)} ${userPrefs['units']['area_in']}`
                : `0 ${userPrefs?.['units']['area_in']}`,
              name: client.name,
              farmCount: client.farmCount ?? 0,
              fieldCount: client.fieldCount ?? 0,
            }))
            setRows(_rows)
            setRowCount(pagination?.total || 0)
          }
          setLoading(false)
        })
        .catch(() => setLoading(false))
    } else {
      setRows([])
      setRowCount(0)
      setLoading(false)
    }
    return () => {
      ignore = true
    }
  }, [
    paginationModel,
    debouncedSearch,
    showClients,
    userPrefs,
    reloadClients,
    showActiveClients,
  ])

  const editClient = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedClient = find(rows, [
        'id',
        rowSelectionModel.ids.values().next().value,
      ])
      dispatch(
        showModal(
          <OCFFEditClientComponent
            selectedClient={selectedClient}
            onSuccess={reload}
          />,
        ),
      )
    }
  }, [rowSelectionModel])

  const deleteClient = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedClients = filter(rows, (row) =>
        rowSelectionModel.ids.has(row.id),
      )
      dispatch(
        showModal(
          <OCFFDeleteClient
            selectedClients={selectedClients}
            onSuccess={reload}
          />,
        ),
      )
    }
  }, [rowSelectionModel])

  const onClick = noop
  const onChangeShowActiveClients = useCallback(() => {
    setShowActiveClients(!showActiveClients)
  }, [showActiveClients])

  const archiveClient = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      dispatch(
        showModal(
          <ArchiveClients
            selectedClients={filter(rows, (row) =>
              rowSelectionModel.ids.has(row.id),
            )}
            onSuccess={reload}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reload])

  const restoreClients = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      dispatch(
        showModal(
          <RestoreClients
            selectedClientIds={Array.from(rowSelectionModel.ids)}
            callback={reload}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reload])

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 4,
          width: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1>{t('clients')}</h1>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                }}
              >
                {t('archived')}
              </Typography>
              <Switch
                size="small"
                checked={showActiveClients}
                onChange={onChangeShowActiveClients}
              />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  marginRight: '10px',
                }}
              >
                {t('active')}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            {showActiveClients ? (
              <>
                <IconButton
                  style={{ display: addModifyClients ? 'inline-flex' : 'none' }}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={archiveClient}
                  title={t('archive_item', { item: t('client') })}
                  size="large"
                >
                  <ArchiveOutlinedIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size >= 1 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>

                <IconButton
                  style={{ display: addModifyClients ? 'inline-flex' : 'none' }}
                  title={t('edit_client')}
                  disabled={
                    rowSelectionModel?.ids?.size === 0 ||
                    rowSelectionModel?.ids?.size > 1
                  }
                  onClick={editClient}
                  size="large"
                >
                  <EditIcon
                    style={{
                      color:
                        rowSelectionModel?.ids?.size === 0 ||
                        rowSelectionModel?.ids?.size > 1
                          ? 'gray'
                          : colors.brand,
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyClients ? 'inline-flex' : 'none' }}
                  onClick={showAddClientPopup}
                  title={t('add_client')}
                  size="large"
                >
                  <AddIcon />
                </IconButton>
              </>
            ) : (
              <>
                {' '}
                <IconButton
                  style={{ display: addModifyClients ? 'inline-flex' : 'none' }}
                  disabled={
                    rowSelectionModel?.ids?.size === 0 ||
                    (rowSelectionModel?.ids?.size > 1 && farms.length === 1)
                  }
                  onClick={deleteClient}
                  title={t('delete_item', { item: t('client') })}
                  size="large"
                >
                  <DeleteOutlineOutlinedIcon
                    style={{
                      color: rowSelectionModel?.ids?.size
                        ? colors.brand
                        : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyClients ? 'inline-flex' : 'none' }}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={restoreClients}
                  title={t('restore_item', {
                    item:
                      rowSelectionModel.ids.size > 1
                        ? t('clients')
                        : t('client'),
                  })}
                  size="large"
                >
                  <UnarchiveOutlinedIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
              </>
            )}

            <TextField
              id="search"
              onChange={(val) => {
                onSearch(val)
              }}
              type="search"
              placeholder="Search"
              hiddenLabel
              sx={{ marginLeft: '10px' }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            overflowX: 'auto',

            // 52px is approx row height, 56px for header/pagination controls
            minHeight: `calc(52px * ${paginationModel?.pageSize || 20} + 56px)`,
          }}
        >
          <DataGrid
            checkboxSelection
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            rows={rows}
            loading={loading}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
              if (selection?.type === 'exclude' && selection?.ids.size === 0) {
                // Setting to include all rows when "select all" is chosen, since DataGrid doesn't provide the list of all row IDs in this case
                // which caused some inconsistencies with the selection state. The rows showed as selected but the selection model was empty,
                // which caused issues with the archive/restore and delete actions.
                const allIds = new Set(rows.map((row) => row.id))
                setRowSelectionModel({ type: 'include', ids: allIds })
              } else {
                setRowSelectionModel(selection)
              }
            }}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={(model: { name: true }) =>
              setColumnVisibility(model)
            }
            initialState={{
              sorting: {
                sortModel: [{ field: 'clientName', sort: 'asc' }],
              },
            }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlayFields,
            }}
            columns={columns}
            pageSizeOptions={[10, 20, 50]}
            onRowClick={(params) => onClick(params.row)}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: colors.greyBackground,
                textTransform: 'uppercase',
              },
            }}
          />
        </Box>
      </Box>
    </>
  )
}
